import React, { useEffect, useMemo, useState } from 'react'
import { lumi } from '../lib/lumi'
import { BarChart3, Calendar, Clock, Download, FileText, Target, TrendingUp, Users } from 'lucide-react'
import toast from 'react-hot-toast'

interface SurveyRecord {
  _id: string
  title: string
  status: 'draft' | 'active' | 'paused' | 'finished'
  createdAt?: string
  updatedAt?: string
  endDate?: string
  isDeleted?: boolean
  deletedAt?: string
}

interface ResponseRecord {
  _id: string
  surveyId: string
  surveyTitle?: string
  status?: 'draft' | 'completed'
  createdAt?: string
  submittedAt?: string
  completionTime?: number
  isDeleted?: boolean
  deletedAt?: string
}

interface ResearcherRecord {
  _id: string
  name: string
  isActive?: boolean
  completedSurveys?: number
}

interface AssignmentRecord {
  _id: string
  surveyId: string
  researcherId: string
  status: 'assigned' | 'in_progress' | 'completed' | 'removed'
  assignedAt?: string
  completedAt?: string
}

interface AnalyticsData {
  surveyMetrics: {
    totalSurveys: number
    activeSurveys: number
    completedSurveys: number
    averageResponseRate: number
  }
  responseMetrics: {
    totalResponses: number
    responsesThisMonth: number
    averageCompletionTime: number
    responsesByStatus: {
      completed: number
      partial: number
      abandoned: number
    }
  }
  researcherMetrics: {
    activeResearchers: number
    averageSurveysPerResearcher: number
    topPerformers: Array<{
      name: string
      completedSurveys: number
    }>
  }
  timeSeriesData: Array<{
    date: string
    responses: number
    surveys: number
  }>
}

const emptyAnalytics: AnalyticsData = {
  surveyMetrics: {
    totalSurveys: 0,
    activeSurveys: 0,
    completedSurveys: 0,
    averageResponseRate: 0
  },
  responseMetrics: {
    totalResponses: 0,
    responsesThisMonth: 0,
    averageCompletionTime: 0,
    responsesByStatus: {
      completed: 0,
      partial: 0,
      abandoned: 0
    }
  },
  researcherMetrics: {
    activeResearchers: 0,
    averageSurveysPerResearcher: 0,
    topPerformers: []
  },
  timeSeriesData: []
}

const toDate = (value?: string) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const isSameMonth = (value?: string) => {
  const date = toDate(value)
  if (!date) return false

  const now = new Date()
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
}

const formatPercent = (part: number, total: number) => {
  if (total <= 0) return 0
  return Math.round((part / total) * 100)
}

const formatMinutes = (seconds: number[]) => {
  if (seconds.length === 0) return 0
  const total = seconds.reduce((sum, value) => sum + value, 0)
  return Math.round((total / seconds.length / 60) * 10) / 10
}

const stripHtml = (html?: string) => {
  if (!html) return ''

  const element = document.createElement('div')
  element.innerHTML = html
  return element.textContent || element.innerText || ''
}

const buildTimeSeries = (days: number, surveys: SurveyRecord[], responses: ResponseRecord[]) => {
  const responseCountByDay = new Map<string, number>()
  const surveyCountByDay = new Map<string, number>()

  responses.forEach((response) => {
    const sourceDate = response.submittedAt || response.createdAt
    const parsed = toDate(sourceDate)
    if (!parsed) return

    const key = parsed.toISOString().split('T')[0]
    responseCountByDay.set(key, (responseCountByDay.get(key) || 0) + 1)
  })

  surveys.forEach((survey) => {
    const parsed = toDate(survey.createdAt)
    if (!parsed) return

    const key = parsed.toISOString().split('T')[0]
    surveyCountByDay.set(key, (surveyCountByDay.get(key) || 0) + 1)
  })

  return Array.from({ length: days }, (_, index) => {
    const current = new Date()
    current.setHours(0, 0, 0, 0)
    current.setDate(current.getDate() - (days - index - 1))
    const key = current.toISOString().split('T')[0]

    return {
      date: key,
      responses: responseCountByDay.get(key) || 0,
      surveys: surveyCountByDay.get(key) || 0
    }
  })
}

const Analytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData>(emptyAnalytics)
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState('30')
  const [selectedMetric, setSelectedMetric] = useState<'responses' | 'surveys'>('responses')
  const [availableSurveys, setAvailableSurveys] = useState<SurveyRecord[]>([])
  const [selectedSurveyId, setSelectedSurveyId] = useState('all')

  useEffect(() => {
    void loadAnalytics()
  }, [dateRange, selectedSurveyId])

  const loadAnalytics = async () => {
    try {
      setLoading(true)

      const [surveysResponse, responsesResponse, researchersResponse, assignmentsResponse] = await Promise.all([
        lumi.entities.surveys.list({ limit: 1000 }),
        lumi.entities.responses.list({ limit: 5000 }),
        lumi.entities.researchers.list({ limit: 1000 }),
        lumi.entities.survey_assignments.list({ limit: 5000 })
      ])

      const surveys = ((surveysResponse.list || []) as SurveyRecord[])
        .filter((survey) => survey.isDeleted !== true && !survey.deletedAt)
      setAvailableSurveys(surveys)

      const surveyScope = selectedSurveyId === 'all'
        ? surveys
        : surveys.filter((survey) => survey._id === selectedSurveyId)
      const scopedSurveyIds = new Set(surveyScope.map((survey) => survey._id))

      const responses = ((responsesResponse.list || []) as ResponseRecord[])
        .filter((response) => !response.isDeleted && !response.deletedAt)
        .filter((response) => selectedSurveyId === 'all' || scopedSurveyIds.has(response.surveyId))
      const researchers = (researchersResponse.list || []) as ResearcherRecord[]
      const assignments = ((assignmentsResponse.list || []) as AssignmentRecord[])
        .filter((assignment) => (
          assignment.status !== 'removed' &&
          (selectedSurveyId === 'all' || scopedSurveyIds.has(assignment.surveyId))
        ))

      const activeResearchers = researchers.filter((researcher) => researcher.isActive).length
      const completedAssignments = assignments.filter((assignment) => assignment.status === 'completed').length
      const partialResponses = responses.filter((response) => response.status === 'draft').length
      const completedResponses = responses.filter((response) => response.status === 'completed').length
      const completionTimes = responses
        .map((response) => response.completionTime)
        .filter((value): value is number => typeof value === 'number' && value > 0)

      const topPerformers = researchers
        .filter((researcher) => researcher.isActive)
        .map((researcher) => {
          const completedByAssignments = assignments.filter((assignment) => (
            assignment.researcherId === researcher._id && assignment.status === 'completed'
          )).length

          return {
            name: researcher.name,
            completedSurveys: completedByAssignments || researcher.completedSurveys || 0
          }
        })
        .filter((researcher) => researcher.completedSurveys > 0)
        .sort((left, right) => right.completedSurveys - left.completedSurveys)
        .slice(0, 5)

      setAnalytics({
        surveyMetrics: {
          totalSurveys: surveyScope.length,
          activeSurveys: surveyScope.filter((survey) => survey.status === 'active').length,
          completedSurveys: surveyScope.filter((survey) => survey.status === 'finished').length,
          averageResponseRate: assignments.length > 0
            ? Math.round((completedAssignments / assignments.length) * 1000) / 10
            : 0
        },
        responseMetrics: {
          totalResponses: responses.length,
          responsesThisMonth: responses.filter((response) => isSameMonth(response.submittedAt || response.createdAt)).length,
          averageCompletionTime: formatMinutes(completionTimes),
          responsesByStatus: {
            completed: completedResponses,
            partial: partialResponses,
            abandoned: 0
          }
        },
        researcherMetrics: {
          activeResearchers,
          averageSurveysPerResearcher: activeResearchers > 0
            ? Math.round((assignments.length / activeResearchers) * 10) / 10
            : 0,
          topPerformers
        },
        timeSeriesData: buildTimeSeries(Number(dateRange), surveyScope, responses)
      })
    } catch (error) {
      console.error('Erro ao carregar analytics:', error)
      toast.error('Erro ao carregar dados de análise')
      setAnalytics(emptyAnalytics)
    } finally {
      setLoading(false)
    }
  }

  const selectedSeries = useMemo(
    () => analytics.timeSeriesData.map((item) => selectedMetric === 'responses' ? item.responses : item.surveys),
    [analytics.timeSeriesData, selectedMetric]
  )

  const chartMax = Math.max(...selectedSeries, 0)
  const hasChartData = chartMax > 0

  const exportReport = () => {
    const rows = [
      ['Indicador', 'Valor'],
      ['Total de Pesquisas', analytics.surveyMetrics.totalSurveys],
      ['Pesquisas Ativas', analytics.surveyMetrics.activeSurveys],
      ['Pesquisas Finalizadas', analytics.surveyMetrics.completedSurveys],
      ['Taxa Média de Resposta', `${analytics.surveyMetrics.averageResponseRate}%`],
      ['Total de Respostas', analytics.responseMetrics.totalResponses],
      ['Respostas neste Mês', analytics.responseMetrics.responsesThisMonth],
      ['Tempo Médio', `${analytics.responseMetrics.averageCompletionTime} min`],
      ['Pesquisadores Ativos', analytics.researcherMetrics.activeResearchers],
      ['Média de Atribuições por Pesquisador', analytics.researcherMetrics.averageSurveysPerResearcher],
      [],
      ['Data', 'Respostas', 'Pesquisas'],
      ...analytics.timeSeriesData.map((item) => [item.date, item.responses, item.surveys]),
    ]

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="utf-8" /></head>
        <body>
          <table>
            <tbody>
              ${rows.map((row) => `<tr>${row.map((cell) => `<td>${String(cell ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `

    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `relatório_analytics_${new Date().toISOString().slice(0, 10)}.xls`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toast.success('Relatório exportado.')
  }

  const MetricCard: React.FC<{
    title: string
    value: string | number
    icon: React.ElementType
    color: string
    subtitle?: string
  }> = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Análises e Relatórios</h1>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <select
            value={selectedSurveyId}
            onChange={(event) => setSelectedSurveyId(event.target.value)}
            className="min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Todas as pesquisas</option>
            {availableSurveys.map((survey) => (
              <option key={survey._id} value={survey._id}>{stripHtml(survey.title)}</option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(event) => setDateRange(event.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7">Últimos 7 dias</option>
            <option value="30">Últimos 30 dias</option>
            <option value="90">Últimos 90 dias</option>
            <option value="365">Último ano</option>
          </select>
          <button
            onClick={exportReport}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
          >
            <Download size={20} />
            <span>Exportar</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total de Pesquisas"
          value={analytics.surveyMetrics.totalSurveys}
          icon={FileText}
          color="bg-blue-600"
          subtitle={`${analytics.surveyMetrics.completedSurveys} finalizadas`}
        />
        <MetricCard
          title="Pesquisas Ativas"
          value={analytics.surveyMetrics.activeSurveys}
          icon={Target}
          color="bg-green-600"
          subtitle="Status real das pesquisas"
        />
        <MetricCard
          title="Total de Respostas"
          value={analytics.responseMetrics.totalResponses}
          icon={BarChart3}
          color="bg-purple-600"
          subtitle={`${analytics.responseMetrics.responsesThisMonth} neste mês`}
        />
        <MetricCard
          title="Taxa Média de Resposta"
          value={`${analytics.surveyMetrics.averageResponseRate}%`}
          icon={TrendingUp}
          color="bg-orange-600"
          subtitle="Concluídas sobre atribuições"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Status das Respostas</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-gray-700">Completas</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900">
                  {analytics.responseMetrics.responsesByStatus.completed}
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  ({formatPercent(
                    analytics.responseMetrics.responsesByStatus.completed,
                    analytics.responseMetrics.totalResponses
                  )}%)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                <span className="text-gray-700">Parciais</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900">
                  {analytics.responseMetrics.responsesByStatus.partial}
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  ({formatPercent(
                    analytics.responseMetrics.responsesByStatus.partial,
                    analytics.responseMetrics.totalResponses
                  )}%)
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span className="text-gray-700">Abandonadas</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900">
                  {analytics.responseMetrics.responsesByStatus.abandoned}
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  ({formatPercent(
                    analytics.responseMetrics.responsesByStatus.abandoned,
                    analytics.responseMetrics.totalResponses
                  )}%)
                </span>
              </div>
            </div>
          </div>

          {analytics.responseMetrics.totalResponses === 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
              Nenhuma resposta registrada ainda.
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Top Pesquisadores</h2>
          {analytics.researcherMetrics.topPerformers.length === 0 ? (
            <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
              Sem histórico suficiente para ranking real no momento.
            </div>
          ) : (
            <div className="space-y-4">
              {analytics.researcherMetrics.topPerformers.map((performer, index) => (
                <div key={`${performer.name}-${index}`} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                      index === 0 ? 'bg-yellow-500' :
                      index === 1 ? 'bg-gray-400' :
                      index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="font-medium text-gray-900">{performer.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-semibold text-gray-900">
                      {performer.completedSurveys}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">conclusões</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="text-blue-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-900">Tempo Médio</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">
            {analytics.responseMetrics.averageCompletionTime} min
          </p>
          <p className="text-sm text-gray-600">Baseado em completionTime real</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Users className="text-green-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-900">Pesquisadores Ativos</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">
            {analytics.researcherMetrics.activeResearchers}
          </p>
          <p className="text-sm text-gray-600">
            Média de {analytics.researcherMetrics.averageSurveysPerResearcher} atribuições por pesquisador
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Calendar className="text-purple-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-900">Este Mês</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">
            {analytics.responseMetrics.responsesThisMonth}
          </p>
          <p className="text-sm text-gray-600">Novas respostas registradas</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Tendência de Volume</h2>
          <select
            value={selectedMetric}
            onChange={(event) => setSelectedMetric(event.target.value as 'responses' | 'surveys')}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="responses">Respostas</option>
            <option value="surveys">Pesquisas</option>
          </select>
        </div>

        {hasChartData ? (
          <div className="h-64 flex items-end justify-between space-x-2 bg-gray-50 rounded-lg p-4">
            {analytics.timeSeriesData.map((item) => {
              const metricValue = selectedMetric === 'responses' ? item.responses : item.surveys
              const height = chartMax > 0 ? Math.max((metricValue / chartMax) * 180, metricValue > 0 ? 6 : 0) : 0

              return (
                <div key={item.date} className="flex flex-col items-center justify-end flex-1 h-full space-y-2">
                  <div
                    className="bg-blue-600 rounded-t w-full max-w-[20px]"
                    style={{ height: `${height}px` }}
                    title={`${metricValue}`}
                  ></div>
                  <span className="text-[10px] text-gray-600 rotate-45 origin-top-left">
                    {new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg text-sm text-gray-600">
            Sem dados reais para gerar a série no período selecionado.
          </div>
        )}

        <div className="mt-4 text-center text-sm text-gray-600">
          Dados reais dos últimos {dateRange} dias
        </div>
      </div>
    </div>
  )
}

export default Analytics
